import React from 'react';
import { PaymentElement } from '@cloudbeds/payment-sdk';

import { createLogLocal } from '../../utils/log-local';

import type { PaymentElementProps, NextAction } from '@cloudbeds/payment-sdk';
import type { WebComponentProps, Guest } from '../@types';

const emptyGuest = { firstName: '', lastName: '', email: '' };
const log = createLogLocal('PaymentElementWrapper');

export const PaymentElementWrapper: React.FC<WebComponentProps> = props => {
  log('render', 'PaymentElementWrapper rendered with props:', props);
  const container = props.container;

  const parseJSON = (
    value: string | undefined,
    defaultValue: any = undefined,
  ) => {
    if (!value) return defaultValue;
    try {
      return JSON.parse(value);
    } catch {
      return defaultValue;
    }
  };

  const parsedProps: PaymentElementProps = {
    allowedPaymentMethods: parseJSON(props.allowedPaymentMethods),
    amount: props.amount ? Number(props.amount) : undefined,
    apiKey: props.apiKey,
    capture: props.capture as 'auto' | 'manual',
    currency: props.currency?.toString()?.toLowerCase() ?? props.currency,
    env: props.env as 'prod' | 'stage' | 'dev' | 'local',
    guest: parseJSON(props.guest, emptyGuest) as Guest,
    labels: parseJSON(props.labels),
    lang: props.lang || 'en',
    paymentMethodFilters: parseJSON(props.paymentMethodFilters),
    propertyId: props.propertyId,
    // @TODO handle redirectAction type properly
    // redirectAction: parseJSON(props.redirectAction),
    scopedCss: props.scopedCss !== 'false',
    theme: parseJSON(props.theme, {}),
    onError: props.onError || container?.onError || container?.['on-error'],
    onChange: props.onChange || container?.onChange || container?.['on-change'],
    onRender: props.onRender || container?.onRender || container?.['on-render'],
    onSubmit: props.onSubmit || container?.onSubmit || container?.['on-submit'],
  };

  // Call the callback with error status if provided
  const handleError = (error: any) => {
    log.error('handleError', 'Error triggered:', error);
    const onError =
      parsedProps?.onError || container?.onError || container?.['on-error'];
    if (onError) onError?.(error);
  };

  const handleChange = (data: any) => {
    log('handleChange', 'Change triggered:', data);
    const onChange =
      parsedProps?.onChange || container?.onChange || container?.['on-change'];
    if (onChange) onChange?.(data);
  };

  const handleRender = (handles: any) => {
    log('handleRender', 'Render triggered with handles:', handles);
    log('handleRender', 'Current props:', props);
    const onRender =
      parsedProps?.onRender || container?.onRender || container?.['on-render'];
    if (onRender) onRender?.(handles);
  };

  const handleSubmit = async (data: any) => {
    log('handleSubmit', 'Submit triggered:', data);
    const onSubmit =
      parsedProps?.onSubmit || container?.onSubmit || container?.['on-submit'];
    if (onSubmit) {
      onSubmit({
        paymentData: data,
        nextAction: { type: 'done' },
        status: 'success',
      } as any);
    }
    return { type: 'done' } as NextAction;
  };

  log('render', 'Rendering PaymentElement with parsedProps:', parsedProps);

  return (
    <PaymentElement
      {...parsedProps}
      onSubmit={handleSubmit}
      onError={handleError}
      onChange={handleChange}
      onRender={handleRender}
    />
  );
};
